import {
    ChevronDownIcon,
    ChevronRightIcon,
    MagnifyingGlassIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Max height of a category's value list before it scrolls (~7 rows).
const VALUE_LIST_MAX_HEIGHT = '14rem';

/**
 * A single selectable option.
 *
 * Plain strings are accepted and normalized to `{ value: s, label: s }`. Use
 * the object form to show a secondary line (`sublabel`) and/or to make the
 * in-menu search match extra terms (`keywords`) — e.g. a Practice option that
 * displays the practice name, shows the doctor underneath, and matches a search
 * on the doctor's name. The selected/stored/emitted value is always `value`, so
 * widening an option from a string to an object never changes what the consumer
 * (or the backend) receives.
 */
export interface FilterOptionItem {
    value: string;
    label: string;
    sublabel?: string;
    keywords?: readonly string[];
}

export type FilterOption = string | FilterOptionItem;

export function normalizeFilterOption(option: FilterOption): FilterOptionItem {
    return typeof option === 'string' ? { value: option, label: option } : option;
}

/** Case-insensitive match across the label, sublabel and any extra keywords. */
export function filterOptionMatches(option: FilterOptionItem, query: string): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    const haystack = [option.label, option.sublabel ?? '', ...(option.keywords ?? [])]
        .join(' ')
        .toLowerCase();
    return haystack.includes(q);
}

export interface MoreMenuCategory {
    key: string;
    label: string;
    options: readonly FilterOption[];
    selectedOptions: string[];
    onChange: (selected: string[]) => void;
    /** Optional node rendered below the category's value list (above Close). */
    footer?: React.ReactNode;
    /**
     * When true the category's active indicator fires even with zero selected
     * options (mirrors FilterDropdown's forceActive behaviour).
     */
    forceActive?: boolean;
    /** One option always stays ticked (radios, no count); active only when it differs from `defaultValue`. */
    selection?: 'single';
    defaultValue?: string;
}

interface MoreFiltersMenuProps {
    /** Categories currently listed inside More (i.e. not promoted to the bar). */
    categories: MoreMenuCategory[];
    /**
     * Every category, regardless of where its button currently lives. The open
     * value view resolves from this list so a category that leaves `categories`
     * mid-interaction (e.g. a responsive un-fold while the menu is open) doesn't
     * vanish from under the user — taking its option list and search input with it.
     */
    allCategories: MoreMenuCategory[];
    /** Number of active filters that are currently hidden inside More (for badge). */
    hiddenActiveCount: number;
}

type ViewState = { type: 'list' } | { type: 'category'; key: string };

const MoreFiltersMenu: React.FC<MoreFiltersMenuProps> = ({
    categories,
    allCategories,
    hiddenActiveCount
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [view, setView] = useState<ViewState>({ type: 'list' });
    const [search, setSearch] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLInputElement>(null);

    // Reset inner view whenever the popover closes.
    useEffect(() => {
        if (!isOpen) {
            setView({ type: 'list' });
            setSearch('');
        }
    }, [isOpen]);

    // Focus search input when entering a category that has enough options.
    useEffect(() => {
        if (view.type === 'category') {
            searchRef.current?.focus();
        }
    }, [view]);

    // Close on outside click or Escape.
    useEffect(() => {
        const handleMouseDown = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        };
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const hasHiddenActive = hiddenActiveCount > 0;

    const isCategoryActive = (cat: MoreMenuCategory) =>
        cat.selection === 'single'
            ? cat.selectedOptions[0] !== cat.defaultValue
            : cat.selectedOptions.length > 0 || (cat.forceActive ?? false);

    // Classic pill chrome, identical to the sibling filter dropdowns; the
    // hidden-active state reads as the same dark count badge they use.
    const buttonClasses = [
        'inline-flex justify-between items-center rounded-full border-2 shadow-sm px-4 py-2 whitespace-nowrap',
        'bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none transition-colors duration-300',
        isOpen ? 'border-gray-700' : 'border-gray-300'
    ].join(' ');

    // Resolve from allCategories, NOT the More-list categories: if the open
    // category leaves `categories` mid-interaction (responsive un-fold), a
    // lookup there would unmount the open value list (and its search) mid-use.
    const activeCategory =
        view.type === 'category' ? (allCategories.find((c) => c.key === view.key) ?? null) : null;

    const filteredCategoryOptions = useMemo<FilterOptionItem[]>(() => {
        if (!activeCategory) return [];
        return activeCategory.options
            .map(normalizeFilterOption)
            .filter((o) => filterOptionMatches(o, search));
    }, [activeCategory, search]);

    const handleCategoryOptionToggle = (value: string) => {
        if (!activeCategory) return;
        if (activeCategory.selection === 'single') {
            if (!activeCategory.selectedOptions.includes(value)) activeCategory.onChange([value]);
            return;
        }
        const next = activeCategory.selectedOptions.includes(value)
            ? activeCategory.selectedOptions.filter((v) => v !== value)
            : [...activeCategory.selectedOptions, value];
        activeCategory.onChange(next);
    };

    return (
        <div className="relative inline-block text-left" ref={containerRef}>
            <button
                type="button"
                className={buttonClasses}
                onClick={() => setIsOpen((prev) => !prev)}
                aria-label="More filters"
            >
                <span>More</span>
                <span className="flex items-center ml-2">
                    <span
                        className={`mr-1 flex items-center justify-center h-5 min-w-5 px-1 rounded-full text-xs leading-none transition-opacity duration-200 ${
                            hasHiddenActive ? 'bg-gray-700 text-white opacity-100' : 'opacity-0'
                        }`}
                    >
                        {hiddenActiveCount}
                    </span>
                    <ChevronDownIcon
                        className={`h-5 w-5 transition-transform duration-300 ${isOpen ? 'transform rotate-180' : ''}`}
                    />
                </span>
            </button>

            {isOpen && (
                // w-64 matches the FilterDropdown menus so the two popover families read identically.
                <div className="absolute right-0 mt-2 w-64 z-30 bg-white border border-gray-200 rounded-[10px] shadow-lg overflow-hidden">
                    {view.type === 'list' ? (
                        <>
                            <div className="px-4 pt-3 pb-2 border-b border-gray-100">
                                <span className="text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                                    More Filters
                                </span>
                            </div>
                            <ul className="py-1">
                                {categories.map((cat) => {
                                    const catActive = isCategoryActive(cat);
                                    const showDot =
                                        catActive &&
                                        (cat.selection === 'single' ||
                                            cat.selectedOptions.length === 0);
                                    return (
                                        <li key={cat.key}>
                                            <button
                                                type="button"
                                                className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                onClick={() => {
                                                    setSearch('');
                                                    setView({ type: 'category', key: cat.key });
                                                }}
                                            >
                                                <span
                                                    className={`flex items-center ${catActive ? 'font-medium text-gray-900' : ''}`}
                                                >
                                                    {cat.label}
                                                    {catActive && (
                                                        <span className="ml-1.5 flex items-center justify-center h-4 min-w-4 px-1 rounded-full text-[10px] leading-none bg-gray-700 text-white">
                                                            {showDot ? (
                                                                <span className="block w-1.5 h-1.5 rounded-full bg-white" />
                                                            ) : (
                                                                cat.selectedOptions.length
                                                            )}
                                                        </span>
                                                    )}
                                                </span>
                                                <ChevronRightIcon className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    ) : (
                        activeCategory && (
                            <>
                                <div className="px-4 pt-3 pb-2 border-b border-gray-100">
                                    <div className="flex items-center justify-between">
                                        <button
                                            type="button"
                                            className="text-xs text-gray-500 hover:text-gray-700 transition-colors"
                                            onClick={() => {
                                                setView({ type: 'list' });
                                                setSearch('');
                                            }}
                                        >
                                            ← Back
                                        </button>
                                        {activeCategory.selection !== 'single' &&
                                            activeCategory.selectedOptions.length > 0 && (
                                                <button
                                                    type="button"
                                                    className="text-xs text-gray-500 underline hover:text-gray-700 transition-colors"
                                                    onClick={() => activeCategory.onChange([])}
                                                >
                                                    clear
                                                </button>
                                            )}
                                    </div>
                                    <p className="mt-1 text-[10.5px] font-semibold tracking-wide text-gray-400 uppercase">
                                        {activeCategory.label}
                                    </p>
                                    <div className="relative mt-2">
                                        <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                        <input
                                            ref={searchRef}
                                            type="text"
                                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-gray-700"
                                            placeholder={`Search ${activeCategory.label.toLowerCase()}...`}
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div
                                    className="overflow-y-auto scrollbar-hide py-1"
                                    style={{ maxHeight: VALUE_LIST_MAX_HEIGHT }}
                                >
                                    {filteredCategoryOptions.length === 0 ? (
                                        <div className="px-4 py-3 text-sm text-gray-400 text-center">
                                            No matches
                                        </div>
                                    ) : (
                                        filteredCategoryOptions.map((option) => {
                                            const checked = activeCategory.selectedOptions.includes(
                                                option.value
                                            );
                                            return (
                                                <div
                                                    key={option.value}
                                                    className="flex items-center px-4 py-2 text-sm hover:bg-gray-100 cursor-pointer"
                                                    onClick={() =>
                                                        handleCategoryOptionToggle(option.value)
                                                    }
                                                >
                                                    <input
                                                        type={
                                                            activeCategory.selection === 'single'
                                                                ? 'radio'
                                                                : 'checkbox'
                                                        }
                                                        className="h-4 w-4 text-gray-700 border-gray-300 rounded focus:ring-gray-500 pointer-events-none"
                                                        checked={checked}
                                                        readOnly
                                                        style={{
                                                            accentColor: checked
                                                                ? '#0A0A0A'
                                                                : '#D4D4D4'
                                                        }}
                                                    />
                                                    <label className="ml-3 min-w-0 flex-1 select-none cursor-pointer">
                                                        <span className="block truncate text-gray-700">
                                                            {option.label}
                                                        </span>
                                                        {option.sublabel && (
                                                            <span className="block truncate text-[11px] text-gray-400">
                                                                {option.sublabel}
                                                            </span>
                                                        )}
                                                    </label>
                                                </div>
                                            );
                                        })
                                    )}
                                </div>
                                {/* Category footer (e.g. split-by-priority toggle for Priority). */}
                                {activeCategory.footer != null && (
                                    <div className="border-t border-gray-100">
                                        {activeCategory.footer}
                                    </div>
                                )}
                                {/* Keep menu open when first value is toggled (handled by not closing on change);
                    the user closes via outside click or Escape per spec. */}
                                <div className="px-4 py-2 border-t border-gray-100">
                                    <button
                                        type="button"
                                        className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                                        onClick={() => setIsOpen(false)}
                                    >
                                        <XMarkIcon className="h-3 w-3" />
                                        Close
                                    </button>
                                </div>
                            </>
                        )
                    )}
                </div>
            )}
        </div>
    );
};

export default MoreFiltersMenu;
